import React, { useState } from 'react';
import styled from 'styled-components';
import { formatSaldo } from '../utils/formatters';
import { BUCKET_ARS, MONEDA_BUCKETS, MONEDA_TITULO } from '../constants/monedas';
import StackedTable from './common/StackedTable';

// Vista de tarjetas en el celular (ver useStackedTable).
const DIFERENCIAS_STACK = { title: 'Nombre' };

const Overlay = styled.div`
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(17, 24, 39, 0.8);
    display: flex; justify-content: center; align-items: center;
    z-index: 2100;
`;

const ModalContent = styled.div`
    background-color: ${props => props.theme.colors.surface};
    padding: 2rem;
    border-radius: 8px;
    width: 94%;
    max-width: 900px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    @media (max-width: 600px) {
        width: calc(100% - 1rem);
        padding: 1rem;
    }
`;

const Title = styled.h3`
    font-family: ${props => props.theme.typography.titleFamily};
    font-size: 1.4rem;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 0.25rem 0;
`;

const Subtitle = styled.p`
    margin: 0 0 1.5rem 0;
    font-size: 0.85rem;
    color: ${props => props.theme.colors.textSecondary};
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const SummaryCard = styled.div`
    background-color: ${props => props.theme.colors.background};
    border-left: 4px solid ${props => props.color};
    border-radius: ${props => props.theme.borderRadius};
    padding: 0.85rem 1rem;
    label {
        display: block;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        color: ${props => props.theme.colors.textSecondary};
    }
    strong {
        display: block;
        font-size: 1.5rem;
        color: ${props => props.color};
        margin-top: 0.2rem;
    }
`;

const Tabs = styled.div`
    display: flex;
    border-bottom: 1px solid ${props => props.theme.colors.lightGray};
    margin-bottom: 1rem;
`;

const TabButton = styled.button`
    background: none;
    border: none;
    padding: 0.5rem 1rem;
    font-weight: 600;
    cursor: pointer;
    border-bottom: 2px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
    color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
    font-family: ${props => props.theme.typography.fontFamily};
`;

const Scroll = styled.div`
    overflow-y: auto;
    flex: 1;
    border: 1px solid ${props => props.theme.colors.lightGray};
    border-radius: ${props => props.theme.borderRadius};
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    th, td {
        padding: 0.6rem 0.85rem;
        text-align: right;
        font-size: 0.85rem;
        border-bottom: 1px solid ${props => props.theme.colors.lightGray};
    }
    th:first-child, td:first-child { text-align: left; }
    th:nth-child(2), td:nth-child(2) { text-align: left; }
    th {
        background-color: #F9FAFB;
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: ${props => props.theme.colors.textSecondary};
        position: sticky;
        top: 0;
    }
    tr:last-child td { border-bottom: none; }
`;

const PosibleCausa = styled.span`
    display: inline-block;
    font-size: 0.7rem;
    background-color: rgba(245, 158, 11, 0.15);
    color: #B45309;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-weight: 600;
`;

const EmptyState = styled.div`
    padding: 2rem;
    text-align: center;
    color: ${props => props.theme.colors.textSecondary};
    font-style: italic;
`;

const Actions = styled.div`
    margin-top: 1.25rem;
    display: flex;
    justify-content: flex-end;
`;

const Button = styled.button`
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.textPrimary};
    border: none;
    border-radius: ${props => props.theme.borderRadius};
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    font-family: ${props => props.theme.typography.fontFamily};
`;

const sugerirCausa = (diferencia, rescatesT1Anteriores) => {
    if (!rescatesT1Anteriores || rescatesT1Anteriores.length === 0) {
        return 'Requiere revisión';
    }
    // Si la diferencia coincide con la suma de rescates T+1 del día anterior, sugerir
    const sumaT1 = rescatesT1Anteriores.reduce((sum, r) => sum + (Number(r.monto) || 0), 0);
    if (Math.abs(sumaT1 - diferencia) < 0.01) {
        return 'Coincide con rescates T+1 del día anterior';
    }
    return 'Requiere revisión';
};

const ConciliacionResultadoModal = ({
    isOpen,
    onClose,
    diferencias,
    totalCuentas,
    rescatesT1PorComitente = {},
}) => {
    const [tab, setTab] = useState(BUCKET_ARS);
    if (!isOpen || !diferencias) return null;

    const cantidadPorBucket = MONEDA_BUCKETS.reduce(
        (acc, bucket) => ({ ...acc, [bucket]: diferencias[bucket]?.length || 0 }), {});
    const total = MONEDA_BUCKETS.reduce((sum, bucket) => sum + cantidadPorBucket[bucket], 0);
    const conciliadas = Math.max(0, (totalCuentas || 0) - total);

    const filas = diferencias[tab] || [];
    const rescatesPorComitente = rescatesT1PorComitente[tab] || {};
    const fmt = (value) => formatSaldo(value, tab);

    return (
        <Overlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <Title>Resultado de la conciliación</Title>
                <Subtitle>
                    Diferencia entre el saldo del custodio (snapshot nuevo) y los movimientos cargados en el sistema desde el snapshot anterior.
                </Subtitle>

                <SummaryGrid>
                    <SummaryCard color="#10B981">
                        <label>Cuentas conciliadas</label>
                        <strong>{conciliadas}</strong>
                    </SummaryCard>
                    {MONEDA_BUCKETS.map(bucket => (
                        <SummaryCard key={bucket} color="#F59E0B">
                            <label>Dif. {MONEDA_TITULO[bucket]}</label>
                            <strong>{cantidadPorBucket[bucket]}</strong>
                        </SummaryCard>
                    ))}
                </SummaryGrid>

                <Tabs>
                    {MONEDA_BUCKETS.map(bucket => (
                        <TabButton key={bucket} active={tab === bucket} onClick={() => setTab(bucket)}>
                            {MONEDA_TITULO[bucket]} ({cantidadPorBucket[bucket]})
                        </TabButton>
                    ))}
                </Tabs>

                <Scroll>
                    {filas.length === 0 ? (
                        <EmptyState>Sin diferencias en {MONEDA_TITULO[tab]}.</EmptyState>
                    ) : (
                        <Table as={StackedTable} stack={DIFERENCIAS_STACK}>
                            <thead>
                                <tr>
                                    <th>Comitente</th>
                                    <th>Nombre</th>
                                    <th>Δ Esperado</th>
                                    <th>Δ Calculado</th>
                                    <th>Diferencia</th>
                                    <th style={{ textAlign: 'left' }}>Posible causa</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filas.map((row) => {
                                    const rescatesT1 = rescatesPorComitente[row.nroComitente] || [];
                                    const causa = sugerirCausa(row.diferencia, rescatesT1);
                                    return (
                                        <tr key={row.nroComitente}>
                                            <td>{row.nroComitente}</td>
                                            <td>{row.nombre}</td>
                                            <td>{fmt(row.deltaEsperado)}</td>
                                            <td>{fmt(row.deltaCalculado)}</td>
                                            <td style={{ fontWeight: 700, color: row.diferencia >= 0 ? '#065F46' : '#991B1B' }}>
                                                {fmt(row.diferencia)}
                                            </td>
                                            <td style={{ textAlign: 'left' }}>
                                                <PosibleCausa>{causa}</PosibleCausa>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Scroll>

                <Actions>
                    <Button onClick={onClose}>Cerrar</Button>
                </Actions>
            </ModalContent>
        </Overlay>
    );
};

export default ConciliacionResultadoModal;
